import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { format } from 'date-fns';
import { baseURL } from '../../../config/api';
import PrintIcon from "@mui/icons-material/Print";
import { formatUSPhone } from '../../../utils/phoneFormatter';

const PurchaseOrderPdf = () => {
    const { id } = useParams();
    const [selectedItems, setSelectedItems] = useState<any[]>([]);
    const [userData, setUserData] = useState<any>({});
    const [vendor, setVendor] = useState<any>({});
    const [isGeneratingPdf, setIsGeneratingPdf] = useState<boolean>(false);

    useEffect(() => {
        if (id) {
            setIsGeneratingPdf(false);
            fetch(`${baseURL}PurchaseOrderDetails/GePurchaseOrderDetailsByPurchaseOrder/${id}`)
                .then(res => res.json())
                .then((res) => {
                    setSelectedItems(res.map((item: any) => ({
                        ...item,
                        qtyOrdered: item.quantityOrdered,
                        qtyReceived: item.quantityReceived
                    })));

                    setIsGeneratingPdf(true);
                })
                .catch((error) => {
                    console.error("Error fetching Project data:", error);
                });

            fetch(`${baseURL}PurchaseOrders/${id}`)
                .then(res => res.json())
                .then((res) => {
                    setUserData(res);
                    getVendor(res.vendorId);

                })
                .catch((error) => {
                    console.error("Error fetching Project data:", error);
                });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id])

    const getVendor = (primaryKey: number) => {
        fetch(`${baseURL}vendors/${primaryKey}`)
            .then(res => res.json())
            .then(res => {
                setVendor(res);
            })
            .catch((error) => {
                console.error("Error fetching Project data:", error);
            });
    }

    return (
        <div className='relative flex min-h-screen flex-col justify-start overflow-hidden bg-gray-50 pb-6 sm:pb-12'>
            <div className="top-0 right-0 left-0 z-50 flex items-center justify-between bg-white px-8 py-3 border-b">
                <div className="text-lg font-semibold text-gray-800">
                    Purchase Order-{id}.pdf
                </div>

                <button
                    onClick={() => window.print()}
                    className="flex items-center gap-2 bg-[#EA8616] hover:bg-[#d97512] text-white px-4 py-2 rounded-lg shadow print:hidden"
                >
                    <PrintIcon fontSize="small" />
                    <span>Print</span>
                </button>
            </div>

            {isGeneratingPdf ?
                <div className="px-16 space-y-8 mt-20">
                    <div className="flex justify-between items-center bg-gradient-to-r from-[#EA8616] to-[#f59e0b] text-white p-6 rounded-xl shadow-lg">
                        <div>
                            <h1 className="text-4xl font-bold tracking-wide">Purchase Order</h1>
                            <p className="text-sm opacity-80">PO #{id}</p>
                        </div>

                        <div className="text-right">
                            <h2 className="text-2xl font-semibold">Express Tire Works</h2>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-2 gap-6">
                        <div className="bg-white rounded-xl shadow p-5 border">
                            <h3 className="text-[#EA8616] text-sm mb-2 uppercase font-semibold">Vendor</h3>
                            <p className="font-semibold text-lg">{vendor.vendorName || "-"}</p>
                            <p className="text-sm text-gray-600">
                                {vendor.address1 || "-"}, {vendor.city || "-"}, {vendor.state || "-"}, {vendor.zipCode || "-"}
                            </p>
                            <p className="text-sm text-gray-600">{formatUSPhone(vendor?.phone || "-")}</p>
                            <p className="text-sm text-gray-600">{vendor.email || "-"}</p>
                        </div>

                        <div className="bg-white rounded-xl shadow p-5 border">
                            <h3 className="text-[#EA8616] text-sm mb-2 uppercase font-semibold">Order Info</h3>
                            <p className="text-sm">
                                <span className="font-semibold">Date: </span>
                                {userData?.dueDate
                                    ? format(new Date(userData?.dueDate), "MMMM dd, yyyy")
                                    : "-"}
                            </p>
                            <p className="text-sm">
                                <span className="font-semibold">Ship Via: </span>
                                {userData?.shipVia || "-"}
                            </p>
                        </div>

                        <div className="bg-white rounded-xl shadow p-5 border">
                            <h3 className="text-[#EA8616] text-sm mb-2 uppercase font-semibold">Shipping Address</h3>
                            <p className="text-sm text-gray-700">
                                {userData?.shipToStreet || "-"}
                            </p>
                            {userData?.shipToStreet2 && (
                                <p className="text-sm text-gray-700">{userData.shipToStreet2}</p>
                            )}
                            <p className="text-sm text-gray-700">
                                {userData?.shipToCity || "-"}, {userData?.shipToState || "-"}
                            </p>
                            <p className="text-sm text-gray-700">
                                {userData?.shipToCountry || "-"} - {userData?.shipToZip || "-"}
                            </p>
                        </div>

                        <div className="bg-white rounded-xl shadow p-5 border">
                            <h3 className="text-[#EA8616] text-sm mb-2 uppercase font-semibold">Billing Address</h3>
                            <p className="text-sm text-gray-700">
                                {userData?.billToStreet || "-"}
                            </p>
                            {userData?.billToStreet2 && (
                                <p className="text-sm text-gray-700">{userData.billToStreet2}</p>
                            )}
                            <p className="text-sm text-gray-700">
                                {userData?.billToCity || "-"}, {userData?.billToState || "-"}
                            </p>
                            <p className="text-sm text-gray-700">
                                {userData?.billToCountry || "-"} - {userData?.billToZip || "-"}
                            </p>
                        </div>
                    </div>

                    <div className="bg-white rounded-xl shadow overflow-hidden">
                        <table className="w-full text-sm">
                            <thead className="bg-primary text-white">
                                <tr>
                                    {["Item #", "Qty Ordered", "Qty Received", "Price", "Total"].map((h, i) => (
                                        <th key={i} className="px-6 py-3 text-left">{h}</th>
                                    ))}
                                </tr>
                            </thead>

                            <tbody>
                                {selectedItems?.map((item, index) => (
                                    <tr
                                        key={index}
                                        className={`border-b ${index % 2 === 0 ? "bg-gray-50" : "bg-white"}`}
                                    >
                                        <td className="px-6 py-3">{item.itemNum}</td>
                                        <td className="px-6 py-3">{item.quantityOrdered}</td>
                                        <td className="px-6 py-3">{item.quantityReceived}</td>
                                        <td className="px-6 py-3">${item.costPer}</td>
                                        <td className="px-6 py-3 font-semibold">
                                            ${(item.quantityOrdered * item.costPer).toFixed(2)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className="flex justify-end">
                        <div className="w-[350px] bg-white rounded-xl shadow border p-5 space-y-3">

                            <div className="flex justify-between text-sm">
                                <span>Subtotal</span>
                                <span>${userData?.totalCost?.toFixed(2) || "0.00"}</span>
                            </div>

                            <div className="flex justify-between text-sm">
                                <span>Discount</span>
                                <span>$0.00</span>
                            </div>

                            <div className="flex justify-between text-sm">
                                <span>Tax</span>
                                <span>$0.00</span>
                            </div>

                            <div className="border-t pt-3 flex justify-between font-bold text-lg text-primary">
                                <span>Grand Total</span>
                                <span>${userData?.totalCost?.toFixed(2) || "0.00"}</span>
                            </div>

                        </div>
                    </div>

                </div>
                :
                <div className="flex flex-col justify-center items-center min-h-[60vh]">

                    <div className="w-12 h-12 border-4 border-[#EA8616] border-t-transparent rounded-full animate-spin"></div>

                    <span className="text-gray-600 mt-4 text-sm">
                        Generating PDF...
                    </span>

                </div>
            }

        </div>
    )
}

export default PurchaseOrderPdf